import React from "react"
import type { Column, ColumnPinningState } from "@tanstack/react-table"
import type { VisibilityState } from "@tanstack/react-table"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button, Icon, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui"
import { useTranslation } from "react-i18next"

const NON_TOGGLEABLE_COLUMNS = new Set(["select", "jobStatus"])

export interface ColumnVisibilityPickerProps {
  columns: Column<any, unknown>[]
  columnVisibility: VisibilityState
  onColumnVisibilityChange: (state: VisibilityState) => void
  columnOrder: string[]
  onColumnOrderChange: (order: string[]) => void
  columnPinning: ColumnPinningState
  onColumnPinningChange: (state: ColumnPinningState) => void
  onReset?: () => void
}

function SortableColumnItem({
  id,
  label,
  isVisible,
  isPinned,
  onToggleVisibility,
  onTogglePin,
}: {
  id: string
  label: string
  isVisible: boolean
  isPinned: boolean
  onToggleVisibility: () => void
  onTogglePin: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm ${
        isDragging ? "z-50 bg-muted shadow-md" : ""
      } ${!isVisible ? "opacity-50" : ""}`}
    >
      <button
        type="button"
        className="shrink-0 cursor-grab touch-none text-muted-foreground/50 hover:text-muted-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <Icon name="grip-vertical" size="xs" />
      </button>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <button
        type="button"
        className={`shrink-0 transition-colors ${isPinned ? "text-primary hover:text-primary/70" : "text-muted-foreground/30 hover:text-muted-foreground"}`}
        onClick={onTogglePin}
        aria-label={isPinned ? "Unpin column" : "Pin column"}
      >
        <Icon name={isPinned ? "pin-off" : "pin"} size="xs" />
      </button>
      <button
        type="button"
        className="shrink-0 text-muted-foreground/50 hover:text-foreground"
        onClick={onToggleVisibility}
        aria-label={isVisible ? "Hide column" : "Show column"}
      >
        <Icon name={isVisible ? "eye" : "eye-off"} size="xs" />
      </button>
    </div>
  )
}

export function ColumnVisibilityPicker({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  columnPinning,
  onColumnPinningChange,
  onReset,
}: ColumnVisibilityPickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  // Close on click outside or Escape
  React.useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      document.removeEventListener("keydown", handleKey)
    }
  }, [open])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const toggleableColumns = columns.filter((col) => !NON_TOGGLEABLE_COLUMNS.has(col.id))
  const pinnedLeftIds = new Set(columnPinning.left ?? [])
  const hasPinnedColumns = toggleableColumns.some((col) => pinnedLeftIds.has(col.id))

  // Use columnOrder for ordering, fall back to column definition order
  const orderedColumnIds = columnOrder.length > 0
    ? columnOrder.filter((id) => toggleableColumns.some((col) => col.id === id))
    : toggleableColumns.map((col) => col.id)

  // Add any columns not yet in the order (e.g. newly added columns)
  const missingIds = toggleableColumns
    .map((col) => col.id)
    .filter((id) => !orderedColumnIds.includes(id))
  const allOrderedIds = [...orderedColumnIds, ...missingIds]

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = allOrderedIds.indexOf(active.id as string)
    const newIndex = allOrderedIds.indexOf(over.id as string)
    const reordered = arrayMove(allOrderedIds, oldIndex, newIndex)

    // Prepend non-toggleable columns to preserve their position
    const nonToggleable = columns
      .filter((col) => NON_TOGGLEABLE_COLUMNS.has(col.id))
      .map((col) => col.id)
    onColumnOrderChange([...nonToggleable, ...reordered])
  }

  return (
    <TooltipProvider delayDuration={400}>
    <div className="relative">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={buttonRef}
            variant="outline"
            size="sm"
            iconStart="columns"
            onClick={() => setOpen((v) => !v)}
          >
            {t("opportunities.table.columns")}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("opportunities.table.columnsTooltip")}</TooltipContent>
      </Tooltip>

      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-background p-2 shadow-lg"
        >
          <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            {t("opportunities.table.manageColumns")}
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={allOrderedIds} strategy={verticalListSortingStrategy}>
              <div className="max-h-80 overflow-y-auto">
                {allOrderedIds.map((colId) => {
                  const isVisible = columnVisibility[colId] !== false
                  const isPinned = pinnedLeftIds.has(colId)
                  return (
                    <SortableColumnItem
                      key={colId}
                      id={colId}
                      label={t(`opportunities.table.${colId}`)}
                      isVisible={isVisible}
                      isPinned={isPinned}
                      onToggleVisibility={() => {
                        onColumnVisibilityChange({
                          ...columnVisibility,
                          [colId]: !isVisible,
                        })
                      }}
                      onTogglePin={() => {
                        const currentLeft = columnPinning.left ?? []
                        const nextLeft = isPinned
                          ? currentLeft.filter((id) => id !== colId)
                          : [...currentLeft, colId]
                        onColumnPinningChange({ ...columnPinning, left: nextLeft })
                      }}
                    />
                  )
                })}
              </div>
            </SortableContext>
          </DndContext>
          {/* Footer actions */}
          {(hasPinnedColumns || onReset) && (
            <div className="mt-1 border-t border-border pt-1">
              {hasPinnedColumns && (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                  onClick={() => {
                    onColumnPinningChange({ left: ["select"] })
                  }}
                >
                  <Icon name="pin-off" size="xs" />
                  {t("opportunities.table.unpinAll")}
                </button>
              )}
              {onReset && (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                  onClick={onReset}
                >
                  <Icon name="refresh-cw" size="xs" />
                  {t("opportunities.table.resetColumns")}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
    </TooltipProvider>
  )
}
